import { useCallback, useEffect, useState } from 'react';
import type { UserProfile, UserProfileInput } from '@runner/shared';
import { apiFetch } from '../api/fetch';
import { useAuth } from '../auth/AuthContext';

interface SaveErrorBody {
  error?: { message?: string };
}

export interface UseProfileResult {
  profile: UserProfile | null;
  loading: boolean;
  saving: boolean;
  save: (input: UserProfileInput) => Promise<UserProfile>;
  refresh: () => Promise<void>;
}

export function useProfile(): UseProfileResult {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Depends on userId (not `user`) so that an AuthContext refresh that only
  // updates avatarUrl / nickname doesn't retrigger profile refetch and clobber
  // the form via the sync effect in ProfilePage.
  const refresh = useCallback(async () => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch('/api/users/me/profile');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { data: UserProfile };
      setProfile(body.data);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // Effect-driven fetch is fire-and-forget; swallow here so the unhandled
    // rejection warning never leaks (test teardown can race fetch mocks).
    // Manual refresh() callers keep normal error propagation.
    refresh().catch(() => {});
  }, [refresh]);

  const save = useCallback(async (input: UserProfileInput): Promise<UserProfile> => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/users/me/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as SaveErrorBody;
        throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      }
      const body = (await res.json()) as { data: UserProfile };
      setProfile(body.data);
      return body.data;
    } finally {
      setSaving(false);
    }
  }, []);

  return { profile, loading, saving, save, refresh };
}
